// ===============================================================
// Forms — Public Form Page (NO auth required)
// Renders a published form for public submission at /forms/public/:slug
// ===============================================================

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, Stack, Loader, ThemeIcon, Button,
} from '@mantine/core';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { fetchTable, TABLES } from '../../lib/api-switch';
import { useFormsStore } from '../../store/formsStore';
import { FormRenderer } from './FormPreview';
import type { FormTemplate, FormSchema } from '../../types/forms';
import { primary } from '../../lib/colors';

// ── Helpers ─────────────────────────────────────────────────────

function parseTemplate(row: Record<string, unknown>): FormTemplate {
  return {
    id: Number(row.id) || 0,
    Rid: Number(row.id) || 0,
    templateName: (row.templateName as string) || (row.template_name as string) || '',
    description: (row.description as string) || '',
    schema: (row.schema as string) || '{}',
    status: ((row.status as string) || 'draft') as FormTemplate['status'],
    module: (row.module as string) || '',
    incidentId: (row.incidentId as string) || (row.incident_id as string) || '',
    isPublic: (row.isPublic as string) || (row.is_public as string) || 'false',
    publicSlug: (row.publicSlug as string) || (row.public_slug as string) || '',
    creatorName: (row.creatorName as string) || (row.creator_name as string) || '',
    responseCount: (row.responseCount as string) || (row.response_count as string) || '0',
    datecreated: (row.datecreated as string) || '',
  };
}

// ── Main Component ──────────────────────────────────────────────

export default function FormPublic() {
  const { slug } = useParams<{ slug: string }>();
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all templates (no auth token — public access)
        const data = await fetchTable(TABLES.formTemplates);
        const templates = data.map(parseTemplate);
        const match = templates.find(t =>
          t.publicSlug === slug && t.isPublic === 'true' && t.status === 'published'
        );
        if (!match) {
          setError('not_found');
          return;
        }
        setTemplate(match);
        try {
          setSchema(JSON.parse(match.schema));
        } catch {
          setError('invalid_schema');
        }
      } catch (err) {
        setError('load_failed');
        console.error('Failed to load form:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // ── Submit handler ──────────────────────────────────────────
  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!template) return;
    setSubmitting(true);
    try {
      const respondentName = String(data.name || data.respondentName || data.full_name || 'Anonymous');
      const respondentEmail = String(data.email || data.respondentEmail || '');

      await useFormsStore.getState().submitResponse({
        templateId: String(template.Rid),
        templateName: template.templateName,
        incidentId: template.incidentId || '',
        respondentName,
        respondentEmail,
        responseData: JSON.stringify(data),
        status: 'complete',
        sourceModule: 'public',
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Submission failed:', err);
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Loader color="white" size="lg" />
      </Box>
    );
  }

  // ── Error states ────────────────────────────────────────────
  if (error) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={480} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="red">
              <IconAlertCircle size={36} />
            </ThemeIcon>
            <Title order={3} ta="center">
              {error === 'not_found' ? 'Form Not Found' : 'Unable to Load Form'}
            </Title>
            <Text ta="center" c="dimmed" size="sm">
              {error === 'not_found'
                ? 'This form does not exist, is no longer accepting responses, or the link may be incorrect.'
                : 'There was a problem loading this form. Please try again later.'}
            </Text>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Success ─────────────────────────────────────────────────
  if (submitted && schema) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={520} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="green">
              <IconCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">
              {schema.settings.successMessage || 'Thank you! Your response has been recorded.'}
            </Title>
            <Text ta="center" c="dimmed" size="sm">
              Your submission has been received. You may close this page.
            </Text>
            {schema.settings.allowMultipleSubmissions && (
              <Button
                variant="light"
                color={primary[700]}
                onClick={() => setSubmitted(false)}
                mt="sm"
              >
                Submit Another Response
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Form ────────────────────────────────────────────────────
  if (!schema) return null;

  return (
    <Box mih="100vh" style={{
      background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
      padding: '40px 16px',
    }}>
      <Paper shadow="lg" radius="md" p="xl" maw={740} mx="auto" pos="relative">
        {submitting && (
          <Box
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-md)',
              zIndex: 10,
            }}
          >
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Submitting...</Text>
            </Stack>
          </Box>
        )}

        <FormRenderer schema={schema} onSubmit={handleSubmit} />
      </Paper>

      {/* Footer */}
      <Text ta="center" size="xs" c="rgba(255,255,255,0.5)" mt="lg">
        Powered by OpenGov Emergency Management
      </Text>
    </Box>
  );
}
